// firebase.js

// Import the functions you need from the Firebase SDKs
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  EmailAuthProvider,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  signInWithPopup,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  updatePassword,
  OAuthProvider,
  sendEmailVerification,
  GithubAuthProvider,
} from 'firebase/auth';
import {
  getDatabase,
  ref as databaseRef,
  onValue,
  get as getDatabaseValue,
  query as databaseQuery,
  orderByChild,
  equalTo,
  //ServerValue,
} from 'firebase/database';
import { getStorage } from 'firebase/storage';

const getFirebaseEnvValue = (modePrefix, key) =>
  import.meta.env[`VITE_${modePrefix}_FIREBASE_${key}`] ??
  import.meta.env[`VITE_FIREBASE_${key}`] ??
  '';

const buildFirebaseConfig = (modePrefix) => ({
  apiKey: getFirebaseEnvValue(modePrefix, 'API_KEY'),
  authDomain: getFirebaseEnvValue(modePrefix, 'AUTH_DOMAIN'),
  databaseURL: getFirebaseEnvValue(modePrefix, 'DATABASE_URL'),
  projectId: getFirebaseEnvValue(modePrefix, 'PROJECT_ID'),
  storageBucket: getFirebaseEnvValue(modePrefix, 'STORAGE_BUCKET'),
  messagingSenderId: getFirebaseEnvValue(modePrefix, 'MESSAGING_SENDER_ID'),
  appId: getFirebaseEnvValue(modePrefix, 'APP_ID'),
  measurementId: getFirebaseEnvValue(modePrefix, 'MEASUREMENT_ID'),
});

const assertFirebaseConfig = (firebaseConfig, modeName) => {
  const requiredEnvMap = {
    apiKey: 'API_KEY',
    authDomain: 'AUTH_DOMAIN',
    databaseURL: 'DATABASE_URL',
    projectId: 'PROJECT_ID',
    storageBucket: 'STORAGE_BUCKET',
    messagingSenderId: 'MESSAGING_SENDER_ID',
    appId: 'APP_ID',
  };
  const missing = Object.keys(requiredEnvMap).filter((k) => !firebaseConfig[k]);
  if (missing.length > 0) {
    throw new Error(
      `Missing Firebase env vars for ${modeName}: ${missing
        .map((k) => `VITE_${modeName}_FIREBASE_${requiredEnvMap[k]}`)
        .join(', ')}`
    );
  }
};

// Prefer Vite MODE so .env.local (VITE_APP_ENV=development) cannot poison production builds.
const environment =
  import.meta.env.MODE === 'production'
    ? 'production'
    : (import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development');

// Choose config based on environment
const firebaseEnvPrefix = environment === 'production' ? 'PROD' : 'DEV';
const config = buildFirebaseConfig(firebaseEnvPrefix);
assertFirebaseConfig(config, firebaseEnvPrefix);

console.log(`Initializing Firebase in ${environment} mode`);

// Initialize Firebase with selected config
const app = initializeApp(config);

// Initialize services
const auth = getAuth(app);
try{
  setPersistence(auth, browserLocalPersistence); // Set persistence to local
}catch(error){
  console.log("firebase.js persistance error");
}


const db = getDatabase(app);
const storage = getStorage(app);

// Initialize providers
const googleProvider = new GoogleAuthProvider();

// Add scopes to the Google provider
// Google Fit scopes are temporarily disabled until app verification is complete.
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.activity.read');
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.location.read');
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.body.read');
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.nutrition.read');
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.blood_pressure.read');
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.blood_glucose.read');
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.oxygen_saturation.read');
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.body_temperature.read');
// googleProvider.addScope('https://www.googleapis.com/auth/fitness.sleep.read');
googleProvider.addScope('https://www.googleapis.com/auth/youtube.readonly');
googleProvider.addScope('https://www.googleapis.com/auth/youtube.force-ssl');

const facebookProvider = new FacebookAuthProvider();
const twitterProvider = new TwitterAuthProvider();
const githubProvider = new GithubAuthProvider();

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');
appleProvider.setCustomParameters({
  // Localize the Apple authentication screen in French.
  locale: 'en',
  clientId: 'cloud.mieros.mie', // Your Services ID
  redirectUri: 'https://mieros-us.firebaseapp.com/__/auth/handler', // Ensure this matches exactly
});

const emailAuthProvider = EmailAuthProvider;

// Class to encapsulate Firebase operations
class Firebase {
  constructor() {
    // Firebase services
    this.auth = auth;
    this.db = db;
    this.storage = storage;

    // Providers
    this.googleProvider = googleProvider;
    this.facebookProvider = facebookProvider;
    this.twitterProvider = twitterProvider;
    this.githubProvider = new GithubAuthProvider();
    this.appleProvider =  appleProvider;

    // Helper
    //this.serverValue = ServerValue;
    this.emailAuthProvider = EmailAuthProvider;

    console.log('Initialized Firebase!');
  }

  // Authentication methods
  doCreateUserWithEmailAndPassword = (email, password) =>
    createUserWithEmailAndPassword(this.auth, email, password);

  doSignInWithEmailAndPassword = (email, password) =>
    signInWithEmailAndPassword(this.auth, email, password);

  doSignInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(this.auth, this.googleProvider);
      // Get the OAuth access token
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential.accessToken;
      
      // Store the token in localStorage for YouTube API access
      localStorage.setItem('googleAccessToken', token);
      
      return result;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  doSignInWithFacebook = () =>
    signInWithPopup(this.auth, this.facebookProvider);

  doSignInWithTwitter = () =>
    signInWithPopup(this.auth, this.twitterProvider);
  // Example method for Apple Sign-In
  doSignInWithApple = () =>
    signInWithPopup(this.auth, this.appleProvider);

  doSignOut = () =>
    signOut(this.auth)
      .then(() => {
        console.log('Firebase Sign-out successful.');
        localStorage.clear();
        window.location.href = '/login';
      })
      .catch((error) => {
        console.log('Firebase Sign-out unsuccessful.', error);
      });

  doPasswordReset = (email) => sendPasswordResetEmail(this.auth, email);

  doPasswordUpdate = (password) => updatePassword(this.auth.currentUser, password);

  doSendEmailVerification = () =>
    sendEmailVerification(this.auth.currentUser, {
      url: import.meta.env.VITE_CONFIRMATION_EMAIL_REDIRECT || window.location.origin,
    });

  // Fetch sign-in methods for email
  fetchSignInMethodsForEmail = (email) => fetchSignInMethodsForEmail(this.auth, email);

  // Get Google Fit data for a specific user
  getGoogleFitData = async (uid) => {
    try {
      const fitRef = databaseRef(this.db, 'googleFitData');
      const fitQuery = databaseQuery(fitRef, orderByChild('uid'), equalTo(uid));
      const snapshot = await getDatabaseValue(fitQuery);
      if (!snapshot.exists()) {
        throw new Error('No Google Fit data found for this user');
      }

      return Object.values(snapshot.val() || {});
    } catch (error) {
      console.error('Error fetching Google Fit data:', error);
      throw error;
    }
  };

  // Check if email exists using Firebase Authentication
  checkIfEmailExistsByFetch = async (email) => {
    try {
      const methods = await fetchSignInMethodsForEmail(this.auth, email);
      console.log('login methods:', methods);
      return methods.length > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  // Query for users in Realtime Database
  checkIfEmailExists = async (email) => {
    const usersRef = databaseRef(this.db, 'users');
    const emailQuery = databaseQuery(usersRef, orderByChild('email'), equalTo(email));
    const snapshot = await getDatabaseValue(emailQuery);
    return snapshot.exists();
  };

  // Check if email exists and get data from Realtime Database
  checkIfEmailExistsAndGetData = async (email) => {
    const usersRef = databaseRef(this.db, 'users');
    const emailQuery = databaseQuery(usersRef, orderByChild('email'), equalTo(email));
    const snapshot = await getDatabaseValue(emailQuery);
    return snapshot.exists() ? snapshot.val() : null;
  };

  // Other methods (e.g., getMieFUUser)
  getMieFUUser = (finduid) => {
    console.log('Getting or looking up mie uid:', finduid);
    console.log('Current user:', this.auth.currentUser);
    console.log('UID from ring:', finduid);
  };

  // Social sign-in method providers
  githubOAuth = () => this.githubProvider;

  twitterOAuth = () => this.twitterProvider;

  facebookOAuth = () => this.facebookProvider;

  
  // On Auth User Listener
  onAuthUserListener = (next, fallback) =>
    this.auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        try {
          const userRef = databaseRef(this.db, `users/${authUser.uid}`);
          const snapshot = await getDatabaseValue(userRef);
          const dbUser = snapshot.val();
  
          if (!dbUser.roles) {
            dbUser.roles = {};
          }
  
          authUser = {
            uid: authUser.uid,
            email: authUser.email,
            emailVerified: authUser.emailVerified,
            providerData: authUser.providerData,
            ...dbUser,
          };
  
          next(authUser);
        } catch (error) {
          console.error('Error fetching user from database:', error);
          fallback();
        }
      } else {
        const localUserData = JSON.parse(localStorage.getItem('userSession'));
        if (localUserData && localUserData.mieToken !== '') {
          next({
            ...localUserData,
            providerData: [{ providerId: 'local' }],
          });
        } else {
          fallback();
        }
      }
    });
  
  // *** User API ***

  // Realtime Database references
  user = (uid) => databaseRef(this.db, `users/${uid}`);
  users = () => databaseRef(this.db, 'users');
  apikeys = () => databaseRef(this.db, 'apikeys');
  mind = () => databaseRef(this.db, 'mind');
  body = () => databaseRef(this.db, 'body');
  soul = () => databaseRef(this.db, 'soul');
  weight = () => databaseRef(this.db, 'weight');

  // User reference alias
  userDoc = (uid) => databaseRef(this.db, `users/${uid}`);

  // *** Message API ***
  message = (uid) => databaseRef(this.db, `messages/${uid}`);
  messages = () => databaseRef(this.db, 'messages');
}


const firebaseInstance = new Firebase();

// Export the initialized Firebase services and providers
export {
  app,
  auth,
  db,
  storage,
  googleProvider,
  facebookProvider,
  twitterProvider,
  githubProvider,
  appleProvider, // Export Apple provider
  emailAuthProvider,
};
